import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api'; // Ensure this is correctly defined
import { format } from 'date-fns'; // For formatting dates
import AuthContext from '../context/AuthContext'; // Import AuthContext
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom'; // For navigating to the edit page

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const Payments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams, setSearchParams] = useState({
        reg_number: '',
        term: '',
        year: '',
        paynow_reference: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1
    });
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const pageSize = 100;

    const { username } = useContext(AuthContext); // Get the username from AuthContext

    const allowedToEditDelete = username === 'sysadmin';

    useEffect(() => {
        fetchPayments();
    }, [pagination.currentPage]);

    const fetchPayments = async () => {
        setLoading(true);
        setError('');

        try {
            let endpoint = `${BASE_URL}/payments/payments`;
            let params = {
                page: pagination.currentPage,
                pageSize
            };

            const hasSearchParams = Object.values(searchParams).some(param => param !== '');

            if (hasSearchParams) {
                endpoint = `${BASE_URL}/payments/payments/search`;
                Object.entries(searchParams).forEach(([key, value]) => {
                    if (value !== '') {
                        params[key] = value;
                    }
                });
            }

            const response = await axios.get(endpoint, { params });

            setPayments(response.data.payments);
            setPagination({
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages
            });
        } catch (err) {
            console.error('Error fetching payments:', err);
            setError('Failed to fetch payments.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prevParams => ({
            ...prevParams,
            [name]: value
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({
            ...prev,
            currentPage: 1
        }));
        fetchPayments();
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    const openModal = (payment) => {
        setSelectedPayment(payment);
        setShowModal(true);
    };

    const closeModal = () => {
        setSelectedPayment(null);
        setShowModal(false);
    };

    const openDeleteModal = (payment) => {
        setSelectedPayment(payment);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setSelectedPayment(null);
        setShowDeleteModal(false);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${BASE_URL}/payments/${selectedPayment.id}`);
            toast.success('Payment deleted successfully.');
            fetchPayments(); // Refresh the payments list
            closeDeleteModal();
        } catch (error) {
            toast.error('Failed to delete payment.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Payments</h1>
                        <div className="h-1 w-full bg-gray-200 mt-2"></div>
                    </div>

                    {/* Search Form */}
                    <form onSubmit={handleSearch} className="mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                                <input
                                    type="text"
                                    name="reg_number"
                                    value={searchParams.reg_number}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    placeholder="e.g., R76539A"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                                <select
                                    name="term"
                                    value={searchParams.term}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                >
                                    <option value="">All Terms</option>
                                    <option value="1">Term 1</option>
                                    <option value="2">Term 2</option>
                                    <option value="3">Term 3</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                <input
                                    type="number"
                                    name="year"
                                    value={searchParams.year}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    placeholder="e.g., 2024"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                                <input
                                    type="text"
                                    name="paynow_reference"
                                    value={searchParams.paynow_reference}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    placeholder="Reference"
                                />
                            </div>
                            <div className="md:col-span-4 flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchParams({
                                            reg_number: '',
                                            term: '',
                                            year: '',
                                            paynow_reference: ''
                                        });
                                        setPagination(prev => ({
                                            ...prev,
                                            currentPage: 1
                                        }));
                                        fetchPayments();
                                    }}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                >
                                    Reset
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-pink-600 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                >
                                    Search
                                </button>
                            </div>
                        </div>
                    </form>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Loading State */}
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <Loader />
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-pink-600 rounded-t-lg">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reg Number</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Txn Ref</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Received Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reported Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Currency</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tr-lg">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {payments.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                                No payments found.
                                            </td>
                                        </tr>
                                    ) : (
                                        payments.map((payment, index) => (
                                            <tr key={payment.id} className={index % 2 === 0 ? 'bg-white' : 'bg-pink-50'}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format(new Date(payment.created_at), 'yyyy-MM-dd HH:mm')}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.reg_number}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.paynow_reference}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.received_amount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.reported_amount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.currency}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => openModal(payment)}
                                                        className="text-blue-600 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                    >
                                                        View
                                                    </button>
                                                    {allowedToEditDelete && (
                                                        <>
                                                            <Link
                                                                to={`/payments/edit/${payment.id}`}
                                                                className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                                            >
                                                                Edit
                                                            </Link>
                                                            <button
                                                                onClick={() => openDeleteModal(payment)}
                                                                className="text-red-600 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination Controls */}
                    <div className="flex justify-between items-center mt-6">
                        <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                            className={`px-4 py-2 rounded-md ${
                                pagination.currentPage === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-pink-600 text-white hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2'
                            }`}
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-700">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                            className={`px-4 py-2 rounded-md ${
                                pagination.currentPage === pagination.totalPages
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-pink-600 text-white hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2'
                            }`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal for Payment Details */}
            {showModal && selectedPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-lg w-full">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Payment Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm font-medium text-gray-500">Reg Number:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.reg_number}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Student Name:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.student_name} {selectedPayment.student_surname}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Form:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.form}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Class Type:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.class_type}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Term:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.term}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Year:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.year}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Payment Method:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.payment_method}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-500">Status:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.status}</p>
                            </div>
                            <div className="col-span-2">
                                <span className="text-sm font-medium text-gray-500">Txn Reference:</span>
                                <p className="mt-1 text-sm text-gray-900">{selectedPayment.paynow_reference}</p>
                            </div>
                            <div className="col-span-2">
                                <span className="text-sm font-medium text-gray-500">Created At:</span>
                                <p className="mt-1 text-sm text-gray-900">{format(new Date(selectedPayment.created_at), 'yyyy-MM-dd HH:mm')}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 text-white bg-pink-600 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for Delete Confirmation */}
            {showDeleteModal && selectedPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-lg w-full">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Deletion</h3>
                        <p className="text-gray-600 mb-4">Are you sure you want to delete this payment? This action cannot be undone and will affect the student's balance.</p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={closeDeleteModal}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer />
        </div>
    );
};

export default Payments;
